<template>
  <div class="places-controls" :class="{ compact }">
    <div class="controls-row">
      <div v-if="$slots.title && !compact" class="controls-title-slot">
        <slot name="title" />
      </div>
      <h2 v-else-if="title && !compact" class="controls-title">{{ title }}</h2>
      <b-input
        class="search-input"
        :value="searchTerm"
        placeholder="Search places"
        @input="$emit('search-change', $event)"
      />
      <div class="source-filter">
        <label>Source</label>
        <b-form-select
          :value="sourceFilter"
          :options="sourceOptions"
          @input="$emit('source-change', $event)"
        />
      </div>
      <b-button
        variant="outline-secondary"
        class="filters-btn"
        :class="{ active: filterPanelOpen }"
        @click="filterPanelOpen = !filterPanelOpen"
      >
        Filters
        <b-badge
          v-if="activeFilterCount > 0"
          variant="primary"
          class="ml-1"
        >
          {{ activeFilterCount }}
        </b-badge>
      </b-button>
    </div>

    <!-- Filter chips and Clear All -->
    <div v-if="activeFilterCount > 0" class="filter-chips-row">
      <span
        v-for="chip in activeFilterChips"
        :key="chip.key"
        class="filter-chip"
      >
        <span class="chip-label">{{ chip.label }}:</span>
        <span class="chip-value">{{ chip.value }}</span>
        <button
          v-if="!chip.required"
          type="button"
          class="chip-remove"
          aria-label="Remove filter"
          @click="$emit('filter-clear', chip.key)"
        >
          &times;
        </button>
      </span>
      <a
        href="#"
        class="clear-all-link"
        @click.prevent="$emit('clear-all-filters')"
      >
        Clear All
      </a>
    </div>

    <!-- Collapsible Filter Panel -->
    <div class="filter-panel-collapse" :class="{ 'is-overlay': compact }">
      <b-collapse :visible="filterPanelOpen">
        <FilterPanel
          :filters="availableFilters"
          :active-filters="activeFilters"
          :active-filter-labels="activeFilterLabels"
          :suggestions="filterSuggestions"
          :loading-filters="filterLoading"
          :error-filters="filterErrors"
          :compact="compact"
          @select="onFilterSelect"
          @clear="onFilterClear"
          @suggest="onFilterSuggest"
        />
      </b-collapse>
    </div>
  </div>
</template>

<script>
import FilterPanel from './FilterPanel.vue';

export default {
  name: 'PlacesControls',
  components: {
    FilterPanel,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    searchTerm: {
      type: String,
      default: '',
    },
    sourceFilter: {
      type: String,
      default: 'All',
    },
    sourceOptions: {
      type: Array,
      default: () => ['All', 'Azira Library'],
    },
    // When true, render a stacked layout tuned for narrow containers
    // (e.g. the place-selection Select tab): controls stack vertically,
    // the title is hidden, and the filter grid collapses to a single column.
    compact: {
      type: Boolean,
      default: false,
    },
    activeFilters: {
      type: Object,
      default: () => ({}),
    },
    activeFilterLabels: {
      type: Object,
      default: () => ({}),
    },
    availableFilters: {
      type: Array,
      default: () => [],
    },
    filterSuggestions: {
      type: Object,
      default: () => ({}),
    },
    filterLoading: {
      type: Object,
      default: () => ({}),
    },
    filterErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      filterPanelOpen: false,
    };
  },
  computed: {
    activeFilterCount() {
      return Object.values(this.activeFilters).filter(v => !!v).length;
    },
    activeFilterChips() {
      return this.availableFilters
        .filter(f => !!this.activeFilters[f.param])
        .map(f => ({
          key: f.param,
          label: f.name,
          value: this.activeFilterLabels[f.param] || this.activeFilters[f.param],
          required: f.required === true,
        }));
    },
  },
  methods: {
    onFilterSelect(filterKey, value) {
      this.$emit('filter-change', { filterKey, value });
    },
    onFilterClear(filterKey) {
      this.$emit('filter-clear', filterKey);
    },
    onFilterSuggest(filterKey, searchText) {
      this.$emit('filter-suggest', filterKey, searchText);
    },
  },
};
</script>

<style lang="scss" scoped>
.places-controls {
  width: 100%;
}

.controls-row {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.controls-title {
  color: #12254b;
  font-size: 2rem;
  margin: 0;
  margin-right: auto;
  white-space: nowrap;
}

.controls-title-slot {
  margin-right: auto;
}

::v-deep .search-input {
  max-width: 520px;

  input {
    border-radius: 999px;
    min-height: 44px;
    padding-left: 1rem;
  }
}

.source-filter {
  align-items: center;
  display: flex;
  gap: 0.75rem;

  label {
    color: #12254b;
    font-weight: 700;
    margin: 0;
  }

  ::v-deep select {
    border-radius: 0.6rem;
    min-width: 135px;
  }
}

.filters-btn {
  border-color: #d8e2ef;
  border-radius: 0.6rem;
  color: #12254b;
  font-weight: 600;
  min-height: 38px;
  white-space: nowrap;

  &:hover,
  &.active {
    background-color: #edf2f9;
    border-color: #b6c6d9;
    color: #12254b;
  }
}

.filter-chips-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.filter-chip {
  align-items: center;
  background-color: #edf2f9;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8rem;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem 0.25rem 0.75rem;
}

.chip-label {
  color: #95aac9;
  font-weight: 600;
}

.chip-value {
  color: #12254b;
  font-weight: 600;
}

.chip-remove {
  align-items: center;
  background: none;
  border: none;
  color: #6e84a3;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  justify-content: center;
  line-height: 1;
  padding: 0 0.2rem;

  &:hover {
    color: #12254b;
  }
}

.clear-all-link {
  color: #2c7be5;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 1000px) {
  .controls-row {
    align-items: stretch;
    flex-direction: column;
  }

  ::v-deep .search-input {
    max-width: 100%;
    width: 100%;
  }
}

// Compact layout for narrow containers (e.g. the Select tab). Driven by a
// prop rather than viewport width, since the crowding is caused by container
// width, not viewport width.
.places-controls.compact {
  // Keep the controls off the container edges (top/bottom/left/right).
  padding: 0.75rem 1rem;
  // Establish the positioning context so the compact filter panel overlay
  // (.filter-panel-collapse.is-overlay) anchors to this controls block.
  // Scoped to compact only; the full-width Places page is unaffected.
  position: relative;

  .controls-row {
    align-items: center;
    // Wrap so the search input + source filter share the first line and the
    // Filters button drops to its own line below.
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: flex-start;
  }

  ::v-deep .search-input {
    // Take the remaining space on the first line next to the source filter.
    flex: 1;
    max-width: 100%;
    min-width: 0;
  }

  .source-filter {
    flex: 0 0 auto;

    ::v-deep select {
      min-width: 120px;
    }
  }

  .filters-btn {
    // Force the button onto its own full-width line beneath the row.
    flex: 1 0 100%;
    width: 100%;
  }

  .filter-chips-row {
    justify-content: flex-start;
  }
}

// Compact-mode filter panel overlay. Takes the collapse wrapper out of flow
// and floats it flush beneath the controls block, so the listings, pagination,
// and "Add Selected Places" button below never shift when filters open. Only
// applied when compact (via the `is-overlay` class hook); non-compact leaves
// the wrapper as an inert in-flow div.
.filter-panel-collapse.is-overlay {
  background: #fff;
  border: 1px solid #d8e2ef;
  border-radius: 0.6rem;
  // Drop shadow separates the floating panel from the listings beneath it
  // (no scrim/dim layer is used).
  box-shadow: 0 0.5rem 1.5rem rgba(18, 37, 75, 0.15);
  left: 0;
  // Cap the overlay to the same vertical space the results list occupies.
  // The Select tab sizes its `.loc-group` list at `calc(100vh - 460px)`, and
  // the overlay's top edge sits at essentially the same point in the column
  // (flush beneath the controls, where the list begins). Matching that family
  // makes the overlay's bottom edge land where the list ends — just above the
  // pagination row and the "Add Selected Places" button — so both stay visible
  // within the fixed-height modal column and the overlay never spills past the
  // visible area. Content taller than this scrolls internally (overflow-y).
  max-height: calc(100vh - 460px);
  // Scroll internally when filter content exceeds the height cap.
  overflow-y: auto;
  // Horizontal breathing room to match the compact controls padding.
  padding: 0 1rem;
  // Anchor flush beneath the controls block, out of document flow, spanning
  // the full controls width.
  position: absolute;
  right: 0;
  top: 100%;
  // Sit above the listings and the WorldLoader overlay (z-index: 10).
  z-index: 20;
}
</style>
